import React from 'react';
import {Divider, Table, Tag} from 'antd';
import {
  attendingDinnerLabel,
  formatCurrencyByLocalChapter,
  meetingPresentationLabel,
} from '../../../../Util/commonUtility';
import useLocalization from '../../../../Localization';
import './style.css';

const isSavedRow = (row) =>
  row?.id &&
  row.id !== '1' &&
  (row.attendee_first_name || row.attendee_email);

const PaymentRegistrationSummary = ({
  attendeesData = [],
  sponsorData,
  totalCartPrice,
  appliedCartCoupon,
  eventLocalChapterData,
  eventType,
  eventISOnline,
  isSponsorCheckout = false,
}) => {
  const {ApplicationString} = useLocalization();
  const hasSponsor =
    isSponsorCheckout && sponsorData && Object.keys(sponsorData).length > 0;
  const vendorReps = isSponsorCheckout
    ? (attendeesData || []).filter(isSavedRow)
    : [];
  const regularAttendees = isSponsorCheckout
    ? []
    : (attendeesData || []).filter(isSavedRow);

  if (!hasSponsor && !vendorReps.length && !regularAttendees.length) {
    return null;
  }

  const renderName = (record) => (
    <Tag prefixCls='regularAttendeesTag'>
      {record.attendee_first_name} {record.attendee_last_name}
    </Tag>
  );

  const renderEmail = (text) => (
    <Tag prefixCls='regularAttendeesTag'>{text}</Tag>
  );

  const renderCompany = (text) => (
    <Tag prefixCls='regularAttendeesTag'>{text}</Tag>
  );

  const renderPrice = (text) => (
    <h4 className='regularAttendees-price'>
      {formatCurrencyByLocalChapter(text, eventLocalChapterData)}
    </h4>
  );

  const renderAttendeeOptions = (record) => {
    const registrationName = record.registration_option?.name;
    const registrationPrice = record.registration_option?.price;
    const foodName =
      record.food_option?.name ||
      ApplicationString['addAttendeesComponent.label.noFoodOption'];
    const foodPrice = record.food_option?.price;
    return (
      <div className='option-tag-container'>
        <Tag prefixCls='regularAttendeesTag'>
          {ApplicationString['addAttendeeComponent.option.registration']}:{' '}
          {registrationName} -{' '}
          {formatCurrencyByLocalChapter(
            registrationPrice,
            eventLocalChapterData,
          )}
        </Tag>
        {eventType?.toLowerCase() !== 'webcast' && !eventISOnline && (
          <Tag prefixCls='regularAttendeesTag'>
            {ApplicationString['addAttendeeComponent.option.food']}: {foodName}
            {foodPrice || foodPrice === 0
              ? ` (${formatCurrencyByLocalChapter(
                  foodPrice,
                  eventLocalChapterData,
                )})`
              : ''}
          </Tag>
        )}
      </div>
    );
  };

  const sponsorshipPerk =
    sponsorData?.event_registration_options?.sponsorship
      ?.event_sponsorship_perk ||
    sponsorData?.event_registration_options?.sponsorship?.eventSponsorshipPerk;

  const renderVendorRepOptions = (record) => {
    const food = record.food_option || record.event_registration_options?.food;
    const foodName = food?.name;
    const foodPrice = food?.price;
    const tags = [];
    if (eventType?.toLowerCase() !== 'webcast' && !eventISOnline && foodName) {
      tags.push(
        <Tag prefixCls='regularAttendeesTag' key='food'>
          {ApplicationString['addAttendeeComponent.option.food']}: {foodName}
          {foodPrice || foodPrice === 0
            ? ` (${formatCurrencyByLocalChapter(
                foodPrice,
                eventLocalChapterData,
              )})`
            : ''}
        </Tag>,
      );
    }
    if (record.attending_presentation) {
      tags.push(
        <Tag prefixCls='regularAttendeesTag' key='presentation'>
          {meetingPresentationLabel(
            sponsorshipPerk?.attendees_price,
            ApplicationString,
          )}
        </Tag>,
      );
    }
    if (record.attending_dinner) {
      tags.push(
        <Tag prefixCls='regularAttendeesTag' key='dinner'>
          {attendingDinnerLabel(
            sponsorshipPerk?.dinner_with_speaker_price,
            ApplicationString,
          )}
        </Tag>,
      );
    }
    if (!tags.length) {
      return null;
    }
    return <div className='option-tag-container'>{tags}</div>;
  };

  const renderSponsorOptions = (record) => {
    const sponsorship =
      record.event_registration_options?.sponsorship || record.sponsorship;
    const name = sponsorship?.name;
    const price = sponsorship?.price ?? record.total_cost;
    return (
      <div className='option-tag-container'>
        <Tag prefixCls='regularAttendeesTag'>
          {
            ApplicationString[
              'paymentCheckoutComponent.registrationSummary.sponsorshipOption'
            ]
          }
          : {name} -{' '}
          {formatCurrencyByLocalChapter(price, eventLocalChapterData)}
        </Tag>
      </div>
    );
  };

  const buildColumns = (renderOption) => [
    {
      title: ApplicationString['addAttendeeComponent.table.title.name'],
      dataIndex: 'name',
      key: 'name',
      width: '18%',
      render: (_, record) => renderName(record),
    },
    {
      title: ApplicationString['addAttendeeComponent.table.title.email'],
      dataIndex: 'attendee_email',
      key: 'email',
      width: '20%',
      render: renderEmail,
    },
    {
      title: ApplicationString['addAttendeeComponent.table.title.option'],
      dataIndex: 'option',
      key: 'option',
      width: '28%',
      render: (_, record) => renderOption(record),
    },
    {
      title: ApplicationString['addAttendeeComponent.table.title.company'],
      dataIndex: 'attendee_company',
      key: 'company',
      width: '16%',
      render: renderCompany,
    },
    {
      title: ApplicationString['addAttendeeComponent.table.title.price'],
      dataIndex: 'total_cost',
      key: 'price',
      width: '10%',
      render: renderPrice,
    },
  ];

  const sponsorship = sponsorData?.event_registration_options?.sponsorship;
  const sponsorRow = hasSponsor
    ? [
        {
          ...sponsorData,
          total_cost: sponsorData?.total_cost ?? sponsorship?.price,
        },
      ]
    : [];

  const showBreakdown =
    totalCartPrice?.discount || totalCartPrice?.refund_credit;

  const totalTableRows = isSponsorCheckout
    ? (hasSponsor ? 1 : 0) + vendorReps.length
    : regularAttendees.length;
  const isSummaryScrollable = totalTableRows > 1;

  const renderTable = (heading, dataSource, columns) => (
    <div className='payment-registration-summary-section'>
      <div className='attendees-label'>{heading}</div>
      <Table
        prefixCls='attendees-table-container'
        pagination={false}
        dataSource={dataSource}
        columns={columns}
        rowKey={(record) => record.id}
      />
    </div>
  );

  const summaryTables = isSponsorCheckout ? (
    <>
      {hasSponsor
        ? renderTable(
            ApplicationString[
              'paymentCheckoutComponent.registrationSummary.heading'
            ],
            sponsorRow,
            buildColumns(renderSponsorOptions),
          )
        : null}
      {vendorReps.length
        ? renderTable(
            ApplicationString[
              'paymentCheckoutComponent.registrationSummary.vendorReps'
            ],
            vendorReps,
            buildColumns(renderVendorRepOptions),
          )
        : null}
    </>
  ) : (
    renderTable(
      ApplicationString[
        'paymentCheckoutComponent.registrationSummary.heading'
      ],
      regularAttendees,
      buildColumns(renderAttendeeOptions),
    )
  );

  return (
    <div className='payment-registration-summary'>
      <div
        className={`payment-registration-summary-tables${
          isSummaryScrollable
            ? ' payment-registration-summary-tables--scrollable'
            : ''
        }`}>
        {summaryTables}
      </div>
      <div className='payment-registration-summary-totals'>
        {appliedCartCoupon ? (
          <div className='payment-registration-summary-coupon'>
            <span>
              {
                ApplicationString[
                  'paymentCheckoutComponent.registrationSummary.couponApplied'
                ]
              }
              :
            </span>
            <strong>{appliedCartCoupon}</strong>
          </div>
        ) : null}
        {showBreakdown ? (
          <div className='payment-registration-summary-breakdown'>
            <div className='discount-price-container'>
              <label className='price-label'>
                {
                  ApplicationString[
                    'userAttendeeCartComponent.credit.subtotal'
                  ]
                }
                :
              </label>
              <p className='price-txt'>
                {formatCurrencyByLocalChapter(
                  totalCartPrice?.total_price_per_cart,
                  eventLocalChapterData,
                )}
              </p>
            </div>
            {totalCartPrice?.discount ? (
              <div className='discount-price-container'>
                <label className='price-label'>
                  {
                    ApplicationString[
                      'userAttendeeCartComponent.credit.discount'
                    ]
                  }
                  :
                </label>
                <p className='price-txt'>
                  {formatCurrencyByLocalChapter(
                    totalCartPrice?.discount,
                    eventLocalChapterData,
                  )}
                </p>
              </div>
            ) : null}
            {totalCartPrice?.refund_credit ? (
              <div className='discount-price-container'>
                <label className='price-label'>
                  {
                    ApplicationString[
                      'userAttendeeCartComponent.credit.useCredit'
                    ]
                  }
                  :
                </label>
                <p className='price-txt'>
                  {formatCurrencyByLocalChapter(
                    totalCartPrice?.refund_credit_for_regular_attendees ??
                      totalCartPrice?.refund_credit,
                    eventLocalChapterData,
                  )}
                </p>
              </div>
            ) : null}
            <Divider prefixCls='price-cart-divider' />
            <div className='discount-price-container'>
              <label className='price-label total-text-cart total-blue'>
                {
                  ApplicationString[
                    'userAttendeeCartComponent.credit.totalAmountToBePaid'
                  ]
                }
              </label>
              <p className='price-txt final-price'>
                {formatCurrencyByLocalChapter(
                  totalCartPrice?.discounted_price,
                  eventLocalChapterData,
                )}
              </p>
            </div>
          </div>
        ) : (
          <div className='payment-registration-summary-total-simple'>
            <label className='price-label total-text-cart total-blue'>
              {
                ApplicationString[
                  'userAttendeeCartComponent.credit.totalAmountToBePaid'
                ]
              }
            </label>
            <p className='price-txt final-price'>
              {formatCurrencyByLocalChapter(
                totalCartPrice?.discounted_price ??
                  totalCartPrice?.total_price_per_cart,
                eventLocalChapterData,
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentRegistrationSummary;
